/**
 * 公式栄養成分表示のページと QR（試用・2026-10-07）— `/public-page`。
 *
 * kalori.jp/r/{チェーン} は、承認された公式値だけを載せる御社の公開ページ（kalori の推定は混ぜない・検索結果には出さない）。
 * ここではその URL と、店頭・メニュー表に載せる QR コード（PNG）を出す。QR は端末で作る（外部のサービスに URL を送らない）。
 */
import { useEffect, useRef, useState } from "react";
import qrcode from "qrcode-generator";
import { api, type LinkedChain } from "../api";

const PUBLIC_ORIGIN = "https://kalori.jp";

export function PublicPage() {
  const [chains, setChains] = useState<LinkedChain[] | null>(null);
  const [published, setPublished] = useState(0);

  useEffect(() => {
    void Promise.all([api.chains(), api.entries()]).then(([c, e]) => {
      if (c.ok) setChains(c.data);
      if (e.ok) setPublished(e.data.filter((x) => x.reviewState === "published").length);
    });
  }, []);

  return (
    <section className="stack-lg">
      <div>
        <h1>
          公開ページと QR <span className="badge trial">試用</span>
        </h1>
        <p className="muted">
          承認された公式値だけを載せた、御社の栄養成分表示のページです（kalori の推定は混ぜません・検索結果には出しません）。値が無い項目は「—」と出ます。アレルゲンは承認された「含む」の品目だけが載ります。QR
          コードを店頭やメニュー表に載せると、お客さまがその場で確かめられます。
        </p>
        {published === 0 && <p className="notice">公開中の公式値がまだ無いので、ページは表示されません（承認されると数分で表示されます）。</p>}
      </div>
      {chains === null && <p className="muted">読み込んでいます…</p>}
      {chains?.map((c) => <ChainQr key={c.shopId} chain={c} />)}
    </section>
  );
}

function ChainQr({ chain }: { chain: LinkedChain }) {
  const url = `${PUBLIC_ORIGIN}/r/${encodeURIComponent(chain.shopId)}`;
  const canvas = useRef<HTMLCanvasElement>(null);
  const [png, setPng] = useState<string | null>(null);

  useEffect(() => {
    const qr = qrcode(0, "M");
    qr.addData(url);
    qr.make();
    const n = qr.getModuleCount();
    const cell = 10;
    const margin = 4 * cell;
    const el = canvas.current;
    if (!el) return;
    el.width = el.height = n * cell + margin * 2;
    const ctx = el.getContext("2d");
    if (!ctx) return;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, el.width, el.height);
    ctx.fillStyle = "#000000";
    for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) if (qr.isDark(r, c)) ctx.fillRect(margin + c * cell, margin + r * cell, cell, cell);
    setPng(el.toDataURL("image/png"));
  }, [url]);

  return (
    <div className="card row-between qr-card">
      <div className="stack">
        <h2>{chain.name}</h2>
        <p>
          <a href={url} target="_blank" rel="noopener noreferrer">
            {url}
          </a>
        </p>
        {png && (
          <p>
            <a className="btn" href={png} download={`kalori-${chain.shopId}-qr.png`}>
              QR コードを保存（PNG）
            </a>
          </p>
        )}
      </div>
      <canvas ref={canvas} className="qr" aria-label={`${chain.name} の公開ページの QR コード`} />
    </div>
  );
}
